import api from 'api/instance';
import { AxiosPromise } from 'axios';
import { Session } from 'types/session';

const URL = `${process.env.REACT_APP_API_URL}`;

type UserData = Pick<Session, 'user'>;

export type LoginPayload = {
  email: string;
  password: string;
};

export type ForgotPasswordPayload = {
  email: string;
};

export type LoginResponse = Session;

const login = async (payload: LoginPayload): Promise<LoginResponse> => {
  const { data } = await api.post<LoginResponse>(`${URL}/auth/login`, payload);
  return data;
};

const googleAuth = async (token: string): Promise<LoginResponse> => {
  const { data } = await api.post<LoginResponse>(`${URL}/auth/google`, {
    token,
  });
  return data;
};

const forgotPassword = async (
  payload: ForgotPasswordPayload,
): Promise<LoginResponse> => {
  const { data } = await api.post<LoginResponse>(
    `${URL}/auth/forgot-password`,
    payload,
  );
  return data;
};

const logout = (): AxiosPromise => api.post(`${URL}/logout`);

export type SignupPayload = {
  fullName: string;
  email: string;
  password: string;
  passwordConfirmation: string;
  phone?: string;
  address?: string;
  state?: string;
  zipcode?: string;
  pilotCertificateNumber?: string;
  companyLicenseNumber?: string;
  companyName?: string;
  companyRegistrationNumber?: string;
};

const signup = async (payload: SignupPayload): Promise<Session> => {
  const { data } = await api.post<Session>(`${URL}/auth/register`, payload);
  return data;
};

const uploadImage = async ({
  image,
  signedUrl,
}: {
  image: File;
  signedUrl: string;
}): Promise<any> => {
  if (!signedUrl) {
    return;
  }

  const data = await fetch(signedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': 'multipart/form-data' },
    body: image,
  });

  return data;
};

export const updateProfilePicture = async (
  file: File,
  uid: string,
  onProgress?: (a: ProgressEvent) => void,
): Promise<any> => {
  const { data } = await api.patch(`${URL}/users/${uid}`, {
    profileImage: file.name,
  });

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.open('PUT', data.signedUrl, true);
    xhr.setRequestHeader('Content-Type', 'multipart/form-data');
    xhr.setRequestHeader('Access-Control-Allow-Origin', '*');
    if (onProgress) {
      xhr.upload.onprogress = onProgress;
    }
    xhr.onload = () => {
      resolve(data);
    };
    xhr.onerror = () => {
      if (xhr.status !== 200) {
        reject(
          new Error(
            `Request failed. Status: ${xhr.status}. Content: ${xhr.responseText}`,
          ),
        );
      }
    };
    xhr.send(file);
  });
};

export const getUser = async (id: string): Promise<UserData> => {
  const { data } = await api.get<UserData>(`${URL}/users/${id}`);

  return data;
};

// temp
export const subscribeToPro = async (uid: string): Promise<Session['user']> => {
  const { data } = await api.patch<Session['user']>(`${URL}/users/${uid}`, {
    pro: true,
  });

  return data;
};

export default {
  login,
  googleAuth,
  logout,
  signup,
  uploadImage,
  forgotPassword,
};
